import { useEffect, useMemo, useRef, useState } from "react";
import HeartbeatPlayerEngine from "./HeartbeatPlayerEngine";

const STORAGE_KEY = "hb_cog_battery_v4";

// Fixed conditions for clean analysis (bpm + amp locked)
const CONDITIONS = [
  { key: "very_slow", label: "Very slow", bpm: 50, amp: 50 },
  { key: "slow", label: "Slow", bpm: 65, amp: 50 },
  { key: "neutral", label: "Neutral", bpm: 80, amp: 50 },
  { key: "fast", label: "Fast", bpm: 105, amp: 50 },
  { key: "very_fast", label: "Very fast", bpm: 130, amp: 50 },
];

// Task durations
const GO_NO_GO_MS = 25000;
const MATCH_PREV_MS = 25000;
const ODD_ONE_OUT_MS = 25000;

/* ---------------------------
   Utility
---------------------------- */

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

function nowMs() {
  return performance.now();
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function median(values) {
  if (!values.length) return null;
  const a = [...values].sort((x, y) => x - y);
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

function mean(values) {
  if (!values.length) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

function std(values) {
  if (values.length < 2) return null;
  const m = mean(values);
  const v = mean(values.map((x) => (x - m) ** 2));
  return Math.sqrt(v);
}

/* ---------------------------
   Tiny inline icons (no deps)
   - All strokes use currentColor so they match button text colour
---------------------------- */

function SvgIcon({ children, size = 18, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color: "currentColor", flex: "0 0 auto", ...style }}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function IconPlay(props) {
  return (
    <SvgIcon {...props}>
      <path d="M8 5v14l11-7L8 5z" />
    </SvgIcon>
  );
}

function IconStop(props) {
  return (
    <SvgIcon {...props}>
      <rect x="7" y="7" width="10" height="10" />
    </SvgIcon>
  );
}

function IconReset(props) {
  return (
    <SvgIcon {...props}>
      <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 7 3" />
      <path d="M19 3v6h-6" />
      <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-7-3" />
      <path d="M5 21v-6h6" />
    </SvgIcon>
  );
}

function IconLogout(props) {
  return (
    <SvgIcon {...props}>
      <path d="M10 17H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h4" />
      <path d="M17 7l5 5-5 5" />
      <path d="M22 12H10" />
    </SvgIcon>
  );
}

function IconDownload(props) {
  return (
    <SvgIcon {...props}>
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 21h14" />
    </SvgIcon>
  );
}

function IconCheck(props) {
  return (
    <SvgIcon {...props}>
      <path d="M20 6L9 17l-5-5" />
    </SvgIcon>
  );
}

function IconX(props) {
  return (
    <SvgIcon {...props}>
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </SvgIcon>
  );
}

/* ---------------------------
   Icon button (icons inherit button text colour)
---------------------------- */

function IconBtn({ children, icon: Icon, style, className = "update-btn", ...props }) {
  return (
    <button
      className={className}
      {...props}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        ...style,
      }}
    >
      {Icon ? <Icon size={18} /> : null}
      <span>{children}</span>
    </button>
  );
}

/* ---------------------------
   Shared UI helpers
---------------------------- */

function BigCard({ children, style }) {
  return (
    <div
      style={{
        margin: "18px auto 10px",
        width: "min(92vw, 740px)",
        borderRadius: 24,
        border: "1px solid rgba(255,255,255,0.18)",
        background: "rgba(255,255,255,0.04)",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.10)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function CountdownOverlay({ n }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: 24,
        background: "rgba(0,0,0,0.20)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 56,
        fontWeight: 900,
        pointerEvents: "none",
      }}
    >
      {n}
    </div>
  );
}

function ScorePill({ text }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "8px 12px",
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(255,255,255,0.04)",
        fontSize: 13,
        opacity: 0.9,
      }}
    >
      {text}
    </div>
  );
}

/* ---------------------------
   Task 1: Go / No-Go
---------------------------- */
function GoNoGoTask({ runId, durationMs, onComplete, onRequestStartAudio }) {
  const [phase, setPhase] = useState("ready"); // ready | running
  const [countdown, setCountdown] = useState(durationMs);

  const [stim, setStim] = useState(null); // null or { type, token, shownAtMs }
  const [feedback, setFeedback] = useState(null); // hit | false_alarm | miss | null
  const [preStart, setPreStart] = useState(null); // null | 3 | 2 | 1

  const [hits, setHits] = useState(0);
  const [falseAlarms, setFalseAlarms] = useState(0);
  const [missesCount, setMissesCount] = useState(0);

  const eventsRef = useRef([]);
  const timerRef = useRef(null);
  const loopRef = useRef(null);
  const endedRef = useRef(false);

  const currentTokenRef = useRef(0);
  const currentTypeRef = useRef(null);
  const tappedThisTokenRef = useRef(false);
  const lastTapTokenRef = useRef(null);

  // calmer timing so it is not stressful
  const stimMs = 650;
  const isiMin = 800;
  const isiMax = 1100;

  function stopAll() {
    if (timerRef.current) clearInterval(timerRef.current);
    if (loopRef.current) clearTimeout(loopRef.current);
    timerRef.current = null;
    loopRef.current = null;
  }

  function flash(kind) {
    setFeedback(kind);
    window.setTimeout(() => setFeedback(null), 160);
  }

  function finish() {
    if (endedRef.current) return;
    endedRef.current = true;
    stopAll();
    setStim(null);
    setPreStart(null);
    setPhase("ready");

    const events = eventsRef.current;
    const goTrials = events.filter((e) => e.kind === "trial" && e.type === "go");
    const nogoTrials = events.filter((e) => e.kind === "trial" && e.type === "nogo");
    const taps = events.filter((e) => e.kind === "tap");
    const misses = events.filter((e) => e.kind === "miss");

    const goTaps = taps.filter((t) => t.targetType === "go");
    const nogoTaps = taps.filter((t) => t.targetType === "nogo");

    const goRTs = goTaps
      .map((t) => t.rtMs)
      .filter((v) => Number.isFinite(v) && v >= 80 && v <= 1500);

    onComplete({
      events,
      summary: {
        task: "go_no_go",
        runId,
        goTrials: goTrials.length,
        nogoTrials: nogoTrials.length,
        goTapCount: goTaps.length,
        nogoTapCount: nogoTaps.length,
        commissionErrors: nogoTaps.length,
        omissionErrors: misses.length,
        medianGoRtMs: median(goRTs),
        meanGoRtMs: mean(goRTs),
        sdGoRtMs: std(goRTs),
        score: {
          hits: goTaps.length,
          falseAlarms: nogoTaps.length,
          misses: misses.length,
          totalGo: goTrials.length,
          totalNoGo: nogoTrials.length,
        },
      },
    });
  }

  function handleTap() {
    if (phase !== "running") return;
    if (!stim) return;

    const token = currentTokenRef.current;
    const type = currentTypeRef.current;
    if (!token || !type) return;

    if (lastTapTokenRef.current === token) return;
    lastTapTokenRef.current = token;

    tappedThisTokenRef.current = true;

    const rtMs = nowMs() - (stim.shownAtMs || nowMs());
    eventsRef.current.push({
      kind: "tap",
      t: nowMs(),
      targetType: type,
      rtMs,
      stimToken: token,
    });

    if (type === "go") setHits((x) => x + 1);
    else setFalseAlarms((x) => x + 1);

    flash(type === "go" ? "hit" : "false_alarm");
  }

  function randIsi() {
    return isiMin + Math.floor(Math.random() * (isiMax - isiMin + 1));
  }

  function loop(startAtMs) {
    if (endedRef.current) return;

    const elapsed = nowMs() - startAtMs;
    if (elapsed >= durationMs) {
      finish();
      return;
    }

    const isNoGo = Math.random() < 0.2;
    const type = isNoGo ? "nogo" : "go";

    const token = (currentTokenRef.current || 0) + 1;
    currentTokenRef.current = token;
    currentTypeRef.current = type;
    tappedThisTokenRef.current = false;
    lastTapTokenRef.current = null;

    const shownAt = nowMs();
    setStim({ type, token, shownAtMs: shownAt });
    eventsRef.current.push({ kind: "trial", t: shownAt, type, token });

    loopRef.current = setTimeout(() => {
      setStim(null);

      if (type === "go" && !tappedThisTokenRef.current) {
        eventsRef.current.push({ kind: "miss", t: nowMs(), stimToken: token });
        setMissesCount((x) => x + 1);
        flash("miss");
      }

      loopRef.current = setTimeout(() => loop(startAtMs), randIsi());
    }, stimMs);
  }

  async function start() {
    if (phase === "running" || preStart !== null) return;

    if (typeof onRequestStartAudio === "function") {
      await onRequestStartAudio();
    }

    setPreStart(3);
    window.setTimeout(() => setPreStart(2), 400);
    window.setTimeout(() => setPreStart(1), 800);

    window.setTimeout(() => {
      setPreStart(null);

      eventsRef.current = [];
      endedRef.current = false;
      currentTokenRef.current = 0;
      currentTypeRef.current = null;
      tappedThisTokenRef.current = false;
      lastTapTokenRef.current = null;
      setStim(null);
      setFeedback(null);

      setHits(0);
      setFalseAlarms(0);
      setMissesCount(0);

      setPhase("running");
      const startAt = nowMs();

      timerRef.current = setInterval(() => {
        const elapsed = nowMs() - startAt;
        const remaining = clamp(durationMs - elapsed, 0, durationMs);
        setCountdown(remaining);
        if (remaining <= 0) finish();
      }, 100);

      loop(startAt);
    }, 1200);
  }

  useEffect(() => {
    return () => stopAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remainingS = Math.ceil(countdown / 1000);
  const isGo = stim?.type === "go";

  const tileBg = !stim
    ? "rgba(255,255,255,0.04)"
    : isGo
    ? "rgba(0, 200, 120, 0.26)"
    : "rgba(255, 80, 80, 0.24)";

  const feedbackText =
    feedback === "hit" ? "✓" : feedback === "false_alarm" ? "✕" : feedback === "miss" ? "!" : "";

  const feedbackBg =
    feedback === "hit"
      ? "rgba(0, 200, 120, 0.18)"
      : feedback === "false_alarm"
      ? "rgba(255, 80, 80, 0.16)"
      : feedback === "miss"
      ? "rgba(255, 200, 0, 0.14)"
      : "transparent";

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
      <h3 style={{ marginTop: 8 }}>Game 1: Tap green, do not tap red</h3>
      <p style={{ opacity: 0.7, marginTop: 6 }}>Tap only when the colour is green.</p>

      <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
        <ScorePill text={`Hits: ${hits}`} />
        <ScorePill text={`False alarms: ${falseAlarms}`} />
        <ScorePill text={`Misses: ${missesCount}`} />
      </div>

      <div
        onPointerDown={handleTap}
        role="button"
        tabIndex={0}
        style={{
          margin: "18px auto 10px",
          width: "min(92vw, 620px)",
          height: "min(62vh, 380px)",
          borderRadius: 24,
          border: "1px solid rgba(255,255,255,0.18)",
          background: tileBg,
          boxShadow: "0 0 0 1px rgba(255,255,255,0.10)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          userSelect: "none",
          cursor: phase === "running" ? "pointer" : "default",
          position: "relative",
          touchAction: "none",
        }}
      >
        {stim ? (
          <div style={{ fontWeight: 900, fontSize: 20, letterSpacing: 0.7, opacity: 0.9 }}>
            {isGo ? "TAP" : "WAIT"}
          </div>
        ) : (
          <div style={{ opacity: 0.4, fontSize: 14 }}> </div>
        )}

        {feedback && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 24,
              background: feedbackBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 50,
              fontWeight: 900,
              opacity: 0.95,
              pointerEvents: "none",
            }}
          >
            {feedbackText}
          </div>
        )}

        {preStart !== null && <CountdownOverlay n={preStart} />}
      </div>

      <p style={{ opacity: 0.7, marginTop: 6 }}>Time left: {remainingS}s</p>

      <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 10, paddingBottom: 6 }}>
        {phase !== "running" ? (
          <IconBtn icon={IconPlay} onClick={start} disabled={preStart !== null}>
            {preStart !== null ? "GET READY" : "START GAME"}
          </IconBtn>
        ) : (
          <IconBtn icon={IconStop} onClick={finish}>
            END EARLY
          </IconBtn>
        )}
      </div>
    </div>
  );
}

/* ---------------------------
   Task 2: Match Previous
---------------------------- */
function MatchPreviousTask({ runId, durationMs, onComplete, onRequestStartAudio }) {
  const items = useMemo(() => ["●", "■", "▲", "◆", "★", "⬟"], []);
  const [phase, setPhase] = useState("ready");
  const [countdown, setCountdown] = useState(durationMs);

  const [leftSym, setLeftSym] = useState(" ");
  const [rightSym, setRightSym] = useState(" ");
  const [feedback, setFeedback] = useState(null); // correct | wrong | null
  const [preStart, setPreStart] = useState(null); // null | 3 | 2 | 1

  const [trialsCount, setTrialsCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);

  const eventsRef = useRef([]);
  const endedRef = useRef(false);
  const tickRef = useRef(null);

  const stepMs = 1100;

  const stream = useMemo(() => {
    const n = Math.max(12, Math.floor(durationMs / stepMs));
    const seq = [];
    let prev = null;

    for (let i = 0; i < n; i++) {
      let sym = items[Math.floor(Math.random() * items.length)];
      if (prev && Math.random() < 0.28) sym = prev;
      seq.push(sym);
      prev = sym;
    }
    return seq;
  }, [durationMs, items, runId]);

  function stopAll() {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
  }

  function flash(kind) {
    setFeedback(kind);
    window.setTimeout(() => setFeedback(null), 160);
  }

  function finish() {
    if (endedRef.current) return;
    endedRef.current = true;
    stopAll();
    setPreStart(null);
    setPhase("ready");

    const events = eventsRef.current;
    const trials = events.filter((e) => e.kind === "trial");
    const responses = events.filter((e) => e.kind === "response");

    const correct = responses.filter((r) => r.correct).length;
    const rtList = responses
      .map((r) => r.rtMs)
      .filter((v) => Number.isFinite(v) && v >= 80 && v <= 2000);

    onComplete({
      events,
      summary: {
        task: "match_previous",
        runId,
        trials: trials.length,
        responses: responses.length,
        accuracy: trials.length ? correct / trials.length : null,
        medianRtMs: median(rtList),
        meanRtMs: mean(rtList),
        sdRtMs: std(rtList),
        score: { correct, wrong: responses.length - correct, total: trials.length },
      },
    });
  }

  function respond(choice) {
    if (phase !== "running") return;

    const lastTrial = [...eventsRef.current].reverse().find((e) => e.kind === "trial");
    if (!lastTrial || lastTrial.answered) return;

    const correct = (choice === "match") === lastTrial.isMatch;
    lastTrial.answered = true;

    const rtMs = nowMs() - lastTrial.shownAtMs;

    eventsRef.current.push({
      kind: "response",
      t: nowMs(),
      idx: lastTrial.idx,
      choice, // match | no_match
      correct,
      rtMs,
    });

    if (correct) setCorrectCount((x) => x + 1);
    else setWrongCount((x) => x + 1);

    flash(correct ? "correct" : "wrong");
  }

  async function start() {
    if (phase === "running" || preStart !== null) return;

    if (typeof onRequestStartAudio === "function") {
      await onRequestStartAudio();
    }

    setPreStart(3);
    window.setTimeout(() => setPreStart(2), 400);
    window.setTimeout(() => setPreStart(1), 800);

    window.setTimeout(() => {
      setPreStart(null);

      eventsRef.current = [];
      endedRef.current = false;
      setFeedback(null);
      setPhase("running");

      setTrialsCount(0);
      setCorrectCount(0);
      setWrongCount(0);

      const startAt = nowMs();
      let idx = 0;

      const first = stream[0];
      setLeftSym(" ");
      setRightSym(first);

      eventsRef.current.push({
        kind: "trial",
        t: nowMs(),
        idx: 0,
        leftSym: " ",
        rightSym: first,
        isMatch: false,
        shownAtMs: nowMs(),
        answered: false,
      });
      setTrialsCount(1);

      idx = 1;

      tickRef.current = setInterval(() => {
        const elapsed = nowMs() - startAt;
        const remaining = clamp(durationMs - elapsed, 0, durationMs);
        setCountdown(remaining);

        if (remaining <= 0 || idx >= stream.length) {
          finish();
          return;
        }

        const prev = stream[idx - 1];
        const next = stream[idx];
        const isMatch = next === prev;

        setLeftSym(prev);
        setRightSym(next);

        eventsRef.current.push({
          kind: "trial",
          t: nowMs(),
          idx,
          leftSym: prev,
          rightSym: next,
          isMatch,
          shownAtMs: nowMs(),
          answered: false,
        });

        setTrialsCount((x) => x + 1);
        idx += 1;
      }, stepMs);
    }, 1200);
  }

  useEffect(() => {
    return () => stopAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remainingS = Math.ceil(countdown / 1000);

  const overlayBg =
    feedback === "correct"
      ? "rgba(0, 200, 120, 0.14)"
      : feedback === "wrong"
      ? "rgba(255, 80, 80, 0.12)"
      : "transparent";

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
      <h3 style={{ marginTop: 8 }}>Game 2: Match the previous</h3>
      <p style={{ opacity: 0.7, marginTop: 6 }}>
        Compare right to left. Press MATCH if they are the same, otherwise NO MATCH.
      </p>

      <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
        <ScorePill text={`Correct: ${correctCount}`} />
        <ScorePill text={`Wrong: ${wrongCount}`} />
        <ScorePill text={`Total: ${trialsCount}`} />
      </div>

      <BigCard style={{ padding: 16, position: "relative" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div
            style={{
              borderRadius: 20,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(255,255,255,0.03)",
              height: 240,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 110,
              fontWeight: 800,
              opacity: phase === "running" ? 0.95 : 0.3,
            }}
          >
            {phase === "running" ? leftSym : " "}
          </div>

          <div
            style={{
              borderRadius: 20,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(255,255,255,0.03)",
              height: 240,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 110,
              fontWeight: 800,
              opacity: phase === "running" ? 0.95 : 0.3,
            }}
          >
            {phase === "running" ? rightSym : " "}
          </div>
        </div>

        {feedback && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 24,
              background: overlayBg,
              pointerEvents: "none",
            }}
          />
        )}

        {preStart !== null && <CountdownOverlay n={preStart} />}
      </BigCard>

      <p style={{ opacity: 0.7, marginTop: 6 }}>Time left: {remainingS}s</p>

      <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 10, paddingBottom: 6 }}>
        {phase !== "running" ? (
          <IconBtn icon={IconPlay} onClick={start} disabled={preStart !== null}>
            {preStart !== null ? "GET READY" : "START GAME"}
          </IconBtn>
        ) : (
          <>
            <IconBtn icon={IconCheck} onClick={() => respond("match")}>
              MATCH
            </IconBtn>
            <IconBtn icon={IconX} onClick={() => respond("no_match")}>
              NO MATCH
            </IconBtn>
            <IconBtn icon={IconStop} onClick={finish}>
              END EARLY
            </IconBtn>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------------------
   Task 3: Odd One Out
   - 2x2 grid of symbols
   - 3 same, 1 different
   - tap the odd one
   - score shown live
   - 3-2-1 countdown
   - starts heartbeat audio on Start
---------------------------- */
function OddOneOutTask({ runId, durationMs, onComplete, onRequestStartAudio }) {
  const [phase, setPhase] = useState("ready"); // ready | running
  const [countdown, setCountdown] = useState(durationMs);
  const [preStart, setPreStart] = useState(null); // null | 3 | 2 | 1
  const [feedback, setFeedback] = useState(null); // correct | wrong | null

  const [grid, setGrid] = useState([" ", " ", " ", " "]);
  const [trialNo, setTrialNo] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);

  const eventsRef = useRef([]);
  const endedRef = useRef(false);
  const tickRef = useRef(null);

  const stepMs = 1200;

  const items = useMemo(() => ["●", "■", "▲", "◆", "★", "⬟", "⬢", "⬣"], []);

  function stopAll() {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
  }

  function flash(kind) {
    setFeedback(kind);
    window.setTimeout(() => setFeedback(null), 160);
  }

  function makeTrial(nextIdx) {
    const base = items[Math.floor(Math.random() * items.length)];
    let odd = items[Math.floor(Math.random() * items.length)];
    while (odd === base) odd = items[Math.floor(Math.random() * items.length)];

    const oddPos = Math.floor(Math.random() * 4);
    const g = [base, base, base, base];
    g[oddPos] = odd;

    const shownAtMs = nowMs();

    setGrid(g);

    eventsRef.current.push({
      kind: "trial",
      t: shownAtMs,
      idx: nextIdx,
      grid: g,
      base,
      odd,
      oddPos,
      shownAtMs,
      answered: false,
    });

    setTrialNo(nextIdx + 1);
  }

  function finish() {
    if (endedRef.current) return;
    endedRef.current = true;
    stopAll();
    setPreStart(null);
    setPhase("ready");

    const events = eventsRef.current;
    const trials = events.filter((e) => e.kind === "trial");
    const responses = events.filter((e) => e.kind === "response");

    const correct = responses.filter((r) => r.correct).length;
    const rtList = responses
      .map((r) => r.rtMs)
      .filter((v) => Number.isFinite(v) && v >= 80 && v <= 2500);

    onComplete({
      events,
      summary: {
        task: "odd_one_out",
        runId,
        trials: trials.length,
        responses: responses.length,
        accuracy: trials.length ? correct / trials.length : null,
        medianRtMs: median(rtList),
        meanRtMs: mean(rtList),
        sdRtMs: std(rtList),
        score: { correct, wrong: responses.length - correct, total: trials.length },
      },
    });
  }

  function respond(pos) {
    if (phase !== "running") return;

    const lastTrial = [...eventsRef.current].reverse().find((e) => e.kind === "trial");
    if (!lastTrial || lastTrial.answered) return;

    lastTrial.answered = true;

    const correct = pos === lastTrial.oddPos;
    const rtMs = nowMs() - lastTrial.shownAtMs;

    eventsRef.current.push({
      kind: "response",
      t: nowMs(),
      idx: lastTrial.idx,
      chosenPos: pos,
      correctPos: lastTrial.oddPos,
      correct,
      rtMs,
    });

    if (correct) setCorrectCount((x) => x + 1);
    else setWrongCount((x) => x + 1);

    flash(correct ? "correct" : "wrong");
  }

  async function start() {
    if (phase === "running" || preStart !== null) return;

    if (typeof onRequestStartAudio === "function") {
      await onRequestStartAudio();
    }

    setPreStart(3);
    window.setTimeout(() => setPreStart(2), 400);
    window.setTimeout(() => setPreStart(1), 800);

    window.setTimeout(() => {
      setPreStart(null);

      eventsRef.current = [];
      endedRef.current = false;
      setFeedback(null);

      setGrid([" ", " ", " ", " "]);
      setTrialNo(0);
      setCorrectCount(0);
      setWrongCount(0);

      setPhase("running");

      const startAt = nowMs();
      setCountdown(durationMs);

      makeTrial(0);
      let idx = 1;

      tickRef.current = setInterval(() => {
        const elapsed = nowMs() - startAt;
        const remaining = clamp(durationMs - elapsed, 0, durationMs);
        setCountdown(remaining);

        if (remaining <= 0) {
          finish();
          return;
        }

        makeTrial(idx);
        idx += 1;
      }, stepMs);
    }, 1200);
  }

  useEffect(() => {
    return () => stopAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remainingS = Math.ceil(countdown / 1000);

  const overlayBg =
    feedback === "correct"
      ? "rgba(0, 200, 120, 0.14)"
      : feedback === "wrong"
      ? "rgba(255, 80, 80, 0.12)"
      : "transparent";

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
      <h3 style={{ marginTop: 8 }}>Game 3: Find the odd one out</h3>
      <p style={{ opacity: 0.7, marginTop: 6 }}>Tap the one that is different.</p>

      <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
        <ScorePill text={`Correct: ${correctCount}`} />
        <ScorePill text={`Wrong: ${wrongCount}`} />
        <ScorePill text={`Total: ${trialNo}`} />
      </div>

      <BigCard style={{ padding: 16, position: "relative" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {grid.map((sym, i) => (
           <button
  key={i}
  type="button"
  className="odd-tile-btn"
  onClick={() => respond(i)}
  disabled={phase !== "running"}
>
  {phase === "running" ? sym : " "}
</button>

          ))}
        </div>

        {feedback && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 24,
              background: overlayBg,
              pointerEvents: "none",
            }}
          />
        )}

        {preStart !== null && <CountdownOverlay n={preStart} />}
      </BigCard>

      <p style={{ opacity: 0.7, marginTop: 6 }}>Time left: {remainingS}s</p>

      <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 10, paddingBottom: 6 }}>
        {phase !== "running" ? (
          <IconBtn icon={IconPlay} onClick={start} disabled={preStart !== null}>
            {preStart !== null ? "GET READY" : "START GAME"}
          </IconBtn>
        ) : (
          <IconBtn icon={IconStop} onClick={finish}>
            END EARLY
          </IconBtn>
        )}
      </div>
    </div>
  );
}

/* ---------------------------
   Ratings
---------------------------- */
function RatingPanel({ onSubmit }) {
  const [calm, setCalm] = useState(null);
  const [alert, setAlert] = useState(null);
  const [comfort, setComfort] = useState(null);

  function ButtonRow({ label, value, setValue }) {
    return (
      <div style={{ marginTop: 10, textAlign: "left" }}>
        <div style={{ fontSize: 13, color: "#6e6a66" }}>
          <strong>{label}</strong>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          {Array.from({ length: 7 }).map((_, i) => {
            const n = i + 1;
            const active = value === n;
            return (
              <button
                key={n}
                className="update-btn"
                onClick={() => setValue(n)}
                style={{ opacity: active ? 1 : 0.55, minWidth: 42 }}
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const ready = calm && alert && comfort;

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 24px" }}>
      <h3 style={{ marginTop: 8, textAlign: "center" }}>Quick ratings</h3>
      <p style={{ opacity: 0.7, marginTop: 6, textAlign: "center" }}>
        Answer quickly. There are no right or wrong answers.
      </p>

      <div
        style={{
          padding: 12,
          borderRadius: 12,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <ButtonRow label="How calm do you feel? (1 low - 7 high)" value={calm} setValue={setCalm} />
        <ButtonRow label="How alert do you feel? (1 low - 7 high)" value={alert} setValue={setAlert} />
        <ButtonRow
          label="How comfortable is the heartbeat sound? (1 low - 7 high)"
          value={comfort}
          setValue={setComfort}
        />
      </div>

      <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 14, paddingBottom: 10 }}>
        <IconBtn
          icon={IconPlay}
          onClick={() => onSubmit({ calm, alert, comfort })}
          disabled={!ready}
          style={{ opacity: ready ? 1 : 0.5, cursor: ready ? "pointer" : "not-allowed" }}
        >
          CONTINUE
        </IconBtn>
      </div>
    </div>
  );
}

/* ---------------------------
   Main component
---------------------------- */
export default function HeartbeatCognitiveBattery({ onExit }) {
  const [screen, setScreen] = useState(-1); // -1 intro, 0.. tasks, "rating", "complete"
  const playerRef = useRef(null);

  const [conditionOrder, setConditionOrder] = useState(CONDITIONS.map((c) => c.key));
  const [conditionIndex, setConditionIndex] = useState(0);
  const [taskIndex, setTaskIndex] = useState(0);

  const [results, setResults] = useState({
    meta: { version: 4, createdAt: new Date().toISOString() },
    conditionOrder: [],
    blocks: [],
    ratings: [],
  });

  const [eligibleConfirmed, setEligibleConfirmed] = useState(false);

  const orderedConditions = useMemo(() => {
    const map = new Map(CONDITIONS.map((c) => [c.key, c]));
    return conditionOrder.map((k) => map.get(k)).filter(Boolean);
  }, [conditionOrder]);

  const currentCondition = orderedConditions[conditionIndex] || null;

  function stopAudio() {
    try {
      if (playerRef.current) playerRef.current.stop();
    } catch {
      // ignore
    }
  }

  async function startAudioForCondition() {
    if (!playerRef.current || !currentCondition) return;

    try {
      playerRef.current.stop();
    } catch {
      // ignore
    }

    try {
      await playerRef.current.play();
    } catch {
      // ignore
    }
  }

  function goBackToWelcome() {
    stopAudio();
    if (typeof onExit === "function") onExit();
    else setScreen(-1);
  }

  function resetAll() {
    stopAudio();
    setScreen(-1);
    setEligibleConfirmed(false);

    const shuffled = shuffle(CONDITIONS.map((c) => c.key));
    setConditionOrder(shuffled);
    setConditionIndex(0);
    setTaskIndex(0);

    setResults({
      meta: { version: 4, createdAt: new Date().toISOString() },
      conditionOrder: shuffled,
      blocks: [],
      ratings: [],
    });

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  // load
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        const shuffled = shuffle(CONDITIONS.map((c) => c.key));
        setConditionOrder(shuffled);
        setResults((r) => ({ ...r, conditionOrder: shuffled }));
        return;
      }

      const parsed = JSON.parse(raw);
      if (typeof parsed?.screen !== "undefined") setScreen(parsed.screen);
      if (typeof parsed?.eligibleConfirmed === "boolean") setEligibleConfirmed(parsed.eligibleConfirmed);

      if (Array.isArray(parsed?.conditionOrder) && parsed.conditionOrder.length) setConditionOrder(parsed.conditionOrder);
      if (typeof parsed?.conditionIndex === "number") setConditionIndex(parsed.conditionIndex);
      if (typeof parsed?.taskIndex === "number") setTaskIndex(parsed.taskIndex);
      if (parsed?.results) setResults(parsed.results);
    } catch {
      const shuffled = shuffle(CONDITIONS.map((c) => c.key));
      setConditionOrder(shuffled);
      setResults((r) => ({ ...r, conditionOrder: shuffled }));
    }
  }, []);

  // save
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          screen,
          eligibleConfirmed,
          conditionOrder,
          conditionIndex,
          taskIndex,
          results,
        })
      );
    } catch {
      // ignore
    }
  }, [screen, eligibleConfirmed, conditionOrder, conditionIndex, taskIndex, results]);

  // stop audio when leaving flow
  useEffect(() => {
    if (screen === -1 || screen === "complete") {
      stopAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  function startBattery() {
    if (!eligibleConfirmed) return;
    setScreen(0);
    setConditionIndex(0);
    setTaskIndex(0);
  }

  function currentTaskName() {
    if (taskIndex === 0) return "go_no_go";
    if (taskIndex === 1) return "match_previous";
    return "odd_one_out";
  }

  function handleTaskComplete(payload) {
    const conditionKey = currentCondition?.key;
    const bpm = currentCondition?.bpm;
    const amp = currentCondition?.amp;

    setResults((prev) => ({
      ...prev,
      blocks: [
        ...prev.blocks,
        {
          id: `${conditionKey}_${currentTaskName()}_${Date.now()}`,
          conditionKey,
          bpm,
          amp,
          ...payload,
        },
      ],
    }));

    const nextTask = taskIndex + 1;
    if (nextTask <= 2) {
      setTaskIndex(nextTask);
      setScreen((s) => (typeof s === "number" ? s + 1 : s));
    } else {
      setScreen("rating");
    }
  }

  function handleRatingSubmit(r) {
    const conditionKey = currentCondition?.key;

    setResults((prev) => ({
      ...prev,
      ratings: [...prev.ratings, { conditionKey, ...r, t: new Date().toISOString() }],
    }));

    // next condition
    const nextCondition = conditionIndex + 1;
    stopAudio();

    if (nextCondition >= orderedConditions.length) {
      setScreen("complete");
    } else {
      setConditionIndex(nextCondition);
      setTaskIndex(0);
      setScreen(0);
    }
  }

  function exportJson() {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hb_cog_results_${new Date().toISOString().slice(0, 19).replaceAll(":", "-")}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const isIntro = screen === -1;
  const isRunning = typeof screen === "number";
  const isRating = screen === "rating";
  const isComplete = screen === "complete";

  const conditionLabel = currentCondition ? `${currentCondition.label} (${currentCondition.bpm} BPM)` : "";
  const conditionProgress = `${conditionIndex + 1} of ${orderedConditions.length}`;

  return (
    <div className="app">
      <img
        src="logo_no_neurotech.png"
        alt="LYEONS Logo"
        className="lyeons-logo"
        style={{ paddingTop: "32px" }}
      />

      <h2 className="title">HEARTBEAT + COGNITIVE GAMES</h2>

      {isIntro && (
        <div style={{ maxWidth: 520, padding: "0 24px", boxSizing: "border-box" }}>
          <p className="subtitle" style={{ maxWidth: "100%", marginBottom: 14 }}>
            <strong>About this session</strong>
            <br />
            A heartbeat sound will play in the background while you complete three short games. This is not medical
            treatment and does not provide diagnosis or medical advice. You may stop at any time.
          </p>

          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: "rgba(255,255,255,0.04)",
              textAlign: "left",
              margin: "0 auto",
              maxWidth: 520,
            }}
          >
            <div style={{ fontSize: 13, color: "#6e6a66", lineHeight: 1.5 }}>
              <strong>Quick guidance</strong>
              <ul style={{ marginTop: 8, marginBottom: 10, paddingLeft: 18 }}>
                <li>Use headphones if possible</li>
                <li>Set your device volume to a comfortable level</li>
                <li>Prioritise accuracy over speed</li>
                <li>If you feel uncomfortable, stop and exit</li>
              </ul>
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 12,
              background: "rgba(255,255,255,0.04)",
              textAlign: "left",
              marginLeft: "auto",
              marginRight: "auto",
              maxWidth: 520,
            }}
          >
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={eligibleConfirmed}
                onChange={(e) => setEligibleConfirmed(e.target.checked)}
                style={{ marginTop: 4 }}
              />
              <span style={{ fontSize: 13, color: "#6e6a66" }}>
                I confirm that I am 18 or over, generally healthy, and would like to take part.
              </span>
            </label>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
              marginTop: 16,
              alignItems: "center",
              width: "100%",
            }}
          >
            <IconBtn
              className="mode-select-button"
              icon={IconPlay}
              onClick={startBattery}
              disabled={!eligibleConfirmed}
              style={{ opacity: eligibleConfirmed ? 1 : 0.5, cursor: eligibleConfirmed ? "pointer" : "not-allowed" }}
            >
              START
            </IconBtn>

            <IconBtn className="mode-select-button" icon={IconLogout} onClick={goBackToWelcome}>
              BACK
            </IconBtn>
          </div>
        </div>
      )}

      {/* Hidden audio engine */}
      {currentCondition && (isRunning || isRating) && (
        <div style={{ position: "absolute", left: -9999, top: -9999, width: 1, height: 1, overflow: "hidden" }}>
          <HeartbeatPlayerEngine
            ref={playerRef}
            bpm={currentCondition.bpm}
            amplitude={currentCondition.amp}
            onBpmChange={() => {}}
            onAmplitudeChange={() => {}}
          />
        </div>
      )}

      {/* Minimal header */}
      {(isRunning || isRating) && currentCondition && (
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
          <h3 style={{ marginTop: 6 }}>{conditionLabel}</h3>
          <p style={{ opacity: 0.7, marginTop: 6 }}>Condition {conditionProgress}</p>
          <p style={{ opacity: 0.65, marginTop: 6 }}>Heartbeat audio will start when you press START GAME.</p>

          <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 10, paddingBottom: 10 }}>
            <IconBtn icon={IconReset} onClick={resetAll}>
              RESET
            </IconBtn>
            <IconBtn icon={IconLogout} onClick={goBackToWelcome}>
              EXIT
            </IconBtn>
          </div>

          <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "6px 0 10px" }} />
        </div>
      )}

      {/* Tasks */}
      {isRunning && currentCondition && (
        <>
          {taskIndex === 0 && (
            <GoNoGoTask
              runId={`${currentCondition.key}_${taskIndex}`}
              durationMs={GO_NO_GO_MS}
              onComplete={handleTaskComplete}
              onRequestStartAudio={startAudioForCondition}
            />
          )}

          {taskIndex === 1 && (
            <MatchPreviousTask
              runId={`${currentCondition.key}_${taskIndex}`}
              durationMs={MATCH_PREV_MS}
              onComplete={handleTaskComplete}
              onRequestStartAudio={startAudioForCondition}
            />
          )}

          {taskIndex === 2 && (
            <OddOneOutTask
              runId={`${currentCondition.key}_${taskIndex}`}
              durationMs={ODD_ONE_OUT_MS}
              onComplete={handleTaskComplete}
              onRequestStartAudio={startAudioForCondition}
            />
          )}
        </>
      )}

      {/* Ratings */}
      {isRating && currentCondition && (
        <div style={{ paddingBottom: 20 }}>
          <RatingPanel onSubmit={handleRatingSubmit} />
        </div>
      )}

      {/* Complete */}
      {isComplete && (
        <div style={{ maxWidth: 720, margin: "0 auto", padding: 24 }}>
          <h3>Complete</h3>
          <p style={{ opacity: 0.8 }}>Thank you. You can export results for analysis.</p>

          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 12,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <div style={{ opacity: 0.9 }}>
              <strong>Blocks recorded:</strong> {results.blocks.length}
            </div>
            <div style={{ opacity: 0.9, marginTop: 6 }}>
              <strong>Ratings recorded:</strong> {results.ratings.length}
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 16 }}>
            <IconBtn icon={IconDownload} onClick={exportJson}>
              EXPORT JSON
            </IconBtn>
            <IconBtn icon={IconReset} onClick={resetAll}>
              RUN AGAIN
            </IconBtn>
            <IconBtn icon={IconLogout} onClick={goBackToWelcome}>
              BACK
            </IconBtn>
          </div>
        </div>
      )}
    </div>
  );
}
